import { useEffect } from 'react';

import { debounce } from 'debounce';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { convertTransition } from 'components/app/_helpers/_app.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { setTransitionEndListener } from 'components/app/storyboard/_mediator/sb.slice';
import { useSbLogger } from 'components/app/storyboard/_mediator/use-sb-logger';

const MIN_DURATION: Seconds = 0.01;

export const useTransitionEnd = (container: React.RefObject<HTMLDivElement>, index: Index) => {
  const { ws } = useAudioTrackContext();
  const dispatch = useAppDispatch();
  const logger = useSbLogger();
  const store = useAppStore();

  useEffect(() => {
    const ref = container.current!;
    const cb = () => {
      const { cells, cursorCell, storyConfig, activePlayerIndex, dampingPlayerIndex } = selectData(store.getState());
      if (!ws.current?.isPlaying()) {
        logger?.regionOutEx(index);
        return;
      }
      if (index !== activePlayerIndex && index !== dampingPlayerIndex) return;
      const cellDuration = cells!.duration[cursorCell];
      const transitionDuration = convertTransition(storyConfig!.transitionTime);
      if (cellDuration - transitionDuration < MIN_DURATION) return;
      dispatch(setTransitionEndListener(index));
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== ref) return;
      debounce(cb, 'onTransitionEnd', 0);
    };
    ref.addEventListener('transitionend', onTransitionEnd);
    return () => {
      ref.removeEventListener('transitionend', onTransitionEnd);
    };
  }, []);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const cursorCell = state.editor__controls.sbPosition.cell;
  const { cells, storyConfig } = state.editor;
  const { activePlayerIndex, dampingPlayerIndex } = state.sb__player;
  return { cells, cursorCell, storyConfig, activePlayerIndex, dampingPlayerIndex };
};
